/**
 * GamesSection - Schedule/Results display for league page
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ExternalLink } from "lucide-react";

export const GamesSection = ({ games, title, emptyMessage, teams, showScores = false, conferences = [], divisions = [], leagueSlug, leagueName }) => {
  const [conferenceFilter, setConferenceFilter] = useState("all");
  const [divisionFilter, setDivisionFilter] = useState("all");
  
  const getTeamInfo = (teamId) => teams?.find(t => t.id === teamId) || null;

  // Filter games by conference and division
  const filteredGames = games.filter(game => {
    const homeTeam = getTeamInfo(game.home_team_id);
    const awayTeam = getTeamInfo(game.away_team_id);
    
    // Conference filter
    if (conferenceFilter !== "all") {
      const matchesConf = homeTeam?.conference_id === conferenceFilter || awayTeam?.conference_id === conferenceFilter;
      if (!matchesConf) return false;
    }
    
    // Division filter - check division entries
    if (divisionFilter !== "all") {
      const homeHasDiv = homeTeam?.division_entries?.some(e => e.division_id === divisionFilter) || 
                         game.home_division_entry_id && teams?.find(t => t.division_entries?.some(e => e.id === game.home_division_entry_id && e.division_id === divisionFilter));
      const awayHasDiv = awayTeam?.division_entries?.some(e => e.division_id === divisionFilter) ||
                         game.away_division_entry_id && teams?.find(t => t.division_entries?.some(e => e.id === game.away_division_entry_id && e.division_id === divisionFilter));
      if (!homeHasDiv && !awayHasDiv) return false;
    }
    
    return true;
  });

  // Group games by date
  const gamesByDate = filteredGames.reduce((acc, game) => {
    const date = game.scheduled_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(game);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const hasFilters = conferences.length > 0 || divisions.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="font-heading text-xl uppercase text-white tracking-wide">
          {title}
        </h2>
        
        {/* Filters */}
        {hasFilters && (
          <div className="flex flex-wrap items-center gap-3">
            {conferences.length > 0 && (
              <select
                value={conferenceFilter}
                onChange={(e) => setConferenceFilter(e.target.value)}
                className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
              >
                <option value="all">All Conferences</option>
                {conferences.map(conf => (
                  <option key={conf.id} value={conf.id}>{conf.name}</option>
                ))}
              </select>
            )}
            {divisions.length > 0 && (
              <select
                value={divisionFilter}
                onChange={(e) => setDivisionFilter(e.target.value)}
                className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
              >
                <option value="all">All Divisions</option>
                {divisions.map(div => (
                  <option key={div.id} value={div.id}>{div.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>
      
      {filteredGames.length === 0 ? (
        <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-12 text-center">
          <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500">{emptyMessage}</p>
        </div>
      ) : (
        Object.entries(gamesByDate).map(([date, dateGames]) => (
          <div key={date} className="space-y-3">
            <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
              {formatDate(date)}
            </h3>
            <div className="space-y-2">
              {dateGames.map(game => (
                <GameCard 
                  key={game.id} 
                  game={game} 
                  getTeamInfo={getTeamInfo}
                  showScores={showScores}
                  leagueSlug={leagueSlug}
                  leagueName={leagueName}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Game Card - Teams link to team pages
const GameCard = ({ game, getTeamInfo, showScores, leagueSlug, leagueName }) => {
  const homeTeam = getTeamInfo(game.home_team_id);
  const awayTeam = getTeamInfo(game.away_team_id);
  
  const getTeamSlug = (team, fallbackName) => {
    return team?.slug || fallbackName?.toLowerCase().replace(/\s+/g, '-');
  };
  
  const awaySlug = getTeamSlug(awayTeam, game.away_team);
  const homeSlug = getTeamSlug(homeTeam, game.home_team);
  
  // Check if there's a broadcast linked
  const hasBroadcast = game.youtube_url || game.recording_id || game.broadcast_id;
  const watchUrl = game.recording_id 
    ? `/on-demand/${game.recording_id}` 
    : game.youtube_url || null;

  return (
    <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-4 hover:border-zinc-700 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {/* Away Team */}
          <div className="flex items-center gap-3 mb-2">
            {awayTeam?.logo_url ? (
              <img src={awayTeam.logo_url} alt="" className="w-8 h-8 object-contain" />
            ) : (
              <div 
                className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold text-white"
                style={{ backgroundColor: awayTeam?.color || "#333" }}
              >
                {awayTeam?.abbreviation || game.away_team?.substring(0, 3) || "AWAY"}
              </div>
            )}
            <Link 
              to={`/team/${awaySlug}`}
              state={{ 
                from: "league", 
                tab: showScores ? "results" : "schedule", 
                leagueSlug: leagueSlug,
                leagueName: leagueName 
              }}
              className="text-white font-medium hover:text-orange-400 transition-colors"
            >
              {awayTeam?.name || game.away_team || "Away Team"}
            </Link>
            {showScores && game.away_score !== undefined && (
              <span className="ml-auto text-xl font-bold text-white">{game.away_score}</span>
            )}
          </div>
          
          {/* Home Team */}
          <div className="flex items-center gap-3">
            {homeTeam?.logo_url ? (
              <img src={homeTeam.logo_url} alt="" className="w-8 h-8 object-contain" />
            ) : (
              <div 
                className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold text-white"
                style={{ backgroundColor: homeTeam?.color || "#333" }}
              >
                {homeTeam?.abbreviation || game.home_team?.substring(0, 3) || "HOME"}
              </div>
            )}
            <Link 
              to={`/team/${homeSlug}`}
              state={{ 
                from: "league", 
                tab: showScores ? "results" : "schedule", 
                leagueSlug: leagueSlug,
                leagueName: leagueName 
              }}
              className="text-white font-medium hover:text-orange-400 transition-colors"
            >
              {homeTeam?.name || game.home_team || "Home Team"}
            </Link>
            {showScores && game.home_score !== undefined && (
              <span className="ml-auto text-xl font-bold text-white">{game.home_score}</span>
            )}
          </div>
        </div>

        {/* Time/Status and Watch Button */}
        <div className="ml-4 flex flex-col items-end gap-2">
          {game.is_live ? (
            <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded animate-pulse">
              LIVE
            </span>
          ) : game.is_completed ? (
            <span className="text-zinc-500 text-sm">Final</span>
          ) : (
            <span className="text-zinc-400 text-sm">{game.scheduled_time || "TBD"}</span>
          )}
          
          {/* Watch Button - only show if broadcast is linked */}
          {hasBroadcast && watchUrl && (
            <Link
              to={watchUrl}
              className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded transition-colors flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Watch
            </Link>
          )}
        </div>
      </div>
      
      {/* Location */}
      {game.location && (
        <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-2 text-xs text-zinc-500">
          <MapPin className="w-3 h-3" />
          {game.location}
        </div>
      )}
    </div>
  );
};

export default GamesSection;
